/**
 * File Manager trash / restore flow.
 *
 * Uses `runAsyncAction` for the spinner + error scaffolding; the multi-list
 * refresh (live + trash) runs inside the async body so both slices are
 * committed before the success toast fires.
 */

import getRouterConfig from "@hooks/useRouter";
import {
  setToastMessage,
  closeDock,
  clearFormFields,
  enableSave,
} from "@actions/global";
import { loadFileLocations } from "@features/files/actions";
import { calculateOffsetLimit } from "@utils/pagination-helpers";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  initialFilesConfig,
  getFileManagerAdapter,
  transformFileLocation,
  type Store,
} from "./files-helpers";
import type { FileThunk } from "./files-init";

export const restoreFile = (
  fileDetails: any,
  _action: string
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(closeDock());

  await runAsyncAction(
    dispatch,
    async () => {
      const { fileLocations, fileAllView } = store.getState();
      const { ctid } = fileLocations;
      const location_name = ctid
        ? fileDetails.file_location.split(`/${ctid}/`)[1]
        : fileDetails.file_location.split("/")[2];
      const filesAdapter = await getFileManagerAdapter(store);

      const apiResult = await filesAdapter.restoreFile(fileDetails.file_id, {
        title: fileDetails.title,
        key_name:
          fileDetails.key_name ||
          `${fileDetails.file_location}/${fileDetails.title}`,
        location: location_name,
        description: fileDetails.description,
        scheduled_date: fileDetails.scheduled_date,
        status: fileDetails.status,
      });
      if (!apiResult.success) throw apiResult.error || "";

      const fileSet = await filesAdapter.fetchAllFiles(
        calculateOffsetLimit(
          { ...initialFilesConfig, status: "live" },
          fileLocations.total
        )
      );
      const updatedFileLocationList = transformFileLocation(
        fileSet.file_locations,
        fileSet.file_manager_base_url,
        fileAllView.filesExtensionsToExclude
      );
      const trashSet = await filesAdapter.fetchTrashFiles(
        calculateOffsetLimit(
          { ...initialFilesConfig, status: "trash" },
          fileLocations.trashTotal
        )
      );
      const updatedTrashList = trashSet.success
        ? transformFileLocation(
            trashSet.trash_files,
            fileSet.file_manager_base_url,
            fileAllView.filesExtensionsToExclude
          )
        : [];
      store.dispatch(
        loadFileLocations(
          updatedFileLocationList,
          fileSet.total,
          updatedTrashList,
          trashSet.total,
          fileSet.file_manager_base_url,
          fileSet.ctid
        )
      );
      store.dispatch(clearFormFields());
    },
    {
      errorAction: "restore",
      errorSubject: "file",
      onSuccess: () => {
        // @ts-expect-error - history is possibly null
        const { history } = getRouterConfig();
        store.dispatch(
          setToastMessage(
            `File ${fileDetails.title} has been restored`,
            history ? history.push("/files") : null
          )
        );
      },
      onFailure: () => {
        store.dispatch(enableSave());
      },
    }
  );
};
